import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleDisclaimer } from "@/components/site/ArticleDisclaimer";
import logoAsset from "@/assets/lifewestway-logo.png.asset.json";
import { makeSeoDescription } from "@/lib/seo-description";

const SITE = "https://lifewestway.com";
const TITLE = "Our Mission & Vision | LifeWestWay";
const DESC =
  "Why LifeWestWay exists: to make moving abroad less confusing, less expensive, and less lonely — one honest, well-researched guide at a time, sourced from official government bodies and updated as rules change.";
const URL_ = `${SITE}/mission-vision`;
const IMAGE = `${SITE}${logoAsset.url}`;

export const Route = createFileRoute("/mission-vision")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: makeSeoDescription(DESC) },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: makeSeoDescription(DESC) },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_ },
      { property: "og:image", content: IMAGE },
      { property: "og:site_name", content: "LifeWestWay" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: makeSeoDescription(DESC) },
      { name: "twitter:image", content: IMAGE },
    ],
    links: [{ rel: "canonical", href: URL_ }],
  }),
  component: MissionVisionPage,
});

const PROMISES = [
  {
    icon: "Sourced information",
    body: "We link to official government resources wherever possible, not just our own opinion.",
  },
  {
    icon: "Honest limitations",
    body: "We tell you clearly when something needs a professional's judgment, not ours.",
  },
  {
    icon: "Regular updates",
    body: "Immigration and finance rules move fast, and we try to move with them.",
  },
  {
    icon: "No false promises",
    body: "We won't tell you a visa is guaranteed, or that a shortcut exists where none does.",
  },
];

function MissionVisionPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Our Mission & Vision",
    url: URL_,
    description: DESC,
    isPartOf: {
      "@type": "WebSite",
      name: "LifeWestWay",
      url: SITE,
    },
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link title="Return to the LifeWestWay homepage" to="/" className="hover:text-accent">
          Home
        </Link>
        <span className="px-2">/</span>
        <Link title="Learn about LifeWestWay and its editorial team" to="/about" className="hover:text-accent">
          About
        </Link>
        <span className="px-2">/</span>
        <span className="text-foreground">Mission & Vision</span>
      </nav>

      {/* H1 Hero */}
      <header className="mt-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
          About LifeWestWay
        </p>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-foreground sm:text-5xl">
          Our Mission & Vision
        </h1>
        <p className="mt-5 max-w-3xl text-lg leading-relaxed text-foreground/75">
          Why we built LifeWestWay, what we're trying to become, and what you should be
          able to expect from every guide on this site.
        </p>
      </header>

      {/* Why We Started This */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">Why We Started This</h2>
        <div className="mt-4 space-y-4 text-sm leading-7 text-foreground/80">
          <p>
            If you're reading this page, there's a good chance you're standing exactly where
            we once stood — staring at a visa checklist at 1 a.m., trying to figure out
            whether the number on your bank statement is actually enough, or wondering if
            the advice you just read on a random forum is even still true.
          </p>
          <p>
            We built LifeWestWay because we got tired of watching people we cared about make
            expensive mistakes simply because the right information was scattered across a
            dozen government PDFs, buried in forum threads full of outdated advice, or
            hidden behind a "book a consultation" paywall before you could even get a
            straight answer.
          </p>
          <p>
            Moving your life to another country — the US, the UK, Australia, Italy,
            Portugal, Canada — is one of the biggest financial and emotional decisions most
            people will ever make. It shouldn't require a law degree to understand your own
            visa requirements, and it shouldn't take three separate government websites to
            figure out how much money you actually need in your account before you apply.
          </p>
        </div>
      </section>

      {/* Our Mission */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">Our Mission</h2>
        <p className="mt-4 text-base font-medium leading-7 text-foreground">
          To make moving abroad less confusing, less expensive, and less lonely — one
          honest, well-researched guide at a time.
        </p>
        <p className="mt-4 text-sm leading-7 text-foreground/80">Concretely, that means:</p>
        <ul className="mt-4 space-y-3 text-sm leading-7 text-foreground/80">
          <li className="flex gap-3">
            <span className="text-accent">•</span>
            <span>
              We check official sources — USCIS, UKVI, the Department of Home Affairs, AIMA,
              IRCC, and others — before we publish a number, not after someone emails us to
              correct it.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-accent">•</span>
            <span>
              We update our guides when rules change, because we know outdated visa or tax
              information doesn't just cause inconvenience — it can genuinely derail
              someone's plans.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-accent">•</span>
            <span>
              We write for the person who's doing this for the first time, without assuming
              they already speak the language of immigration lawyers or accountants.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-accent">•</span>
            <span>
              We're upfront when something is a general estimate rather than a guarantee,
              because we'd rather you trust us a little less on one detail than trust us
              completely on something wrong.
            </span>
          </li>
        </ul>
      </section>

      {/* Our Vision */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">Our Vision</h2>
        <div className="mt-4 space-y-4 text-sm leading-7 text-foreground/80">
          <p>
            We want LifeWestWay to become the place people bookmark before they even book
            their flight — not because we're the flashiest immigration site out there, but
            because we're the one that's still right six months later, and the one that
            treated their situation like it mattered.
          </p>
          <p>
            We're not trying to replace immigration lawyers, tax accountants, or registered
            migration agents — for anything genuinely complex or high-stakes, we tell people
            to go talk to one. What we're trying to do is make sure that by the time someone
            walks into that consultation, they already understand the basics well enough to
            ask the right questions and not get taken advantage of.
          </p>
        </div>
      </section>

      {/* A Personal Note */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">A Personal Note</h2>
        <div className="mt-4 space-y-4 text-sm leading-7 text-foreground/80">
          <p>
            A lot of what's on this site exists because someone — a friend, a family member,
            a colleague — hit a wall we wished they hadn't had to hit. A student who nearly
            got their visa refused over a bank statement that was three days short of the
            required 28-day window. A retiree who found out about a tax regime closing two
            weeks after it would have saved them thousands. A newcomer who didn't realize
            their credit history simply didn't exist in their new country, and had to start
            completely from zero.
          </p>
          <p>Every guide on this site is written with one of those situations in mind. We'd rather you never have to learn these lessons the hard way.</p>
        </div>
      </section>

      {/* What This Means for You */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">
          What This Means for You
        </h2>
        <p className="mt-4 text-sm leading-7 text-foreground/80">
          When you read something on LifeWestWay, you should be able to expect:
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {PROMISES.map((p) => (
            <div
              key={p.icon}
              className="rounded-xl border border-border bg-card/60 p-5 transition-colors hover:border-accent/40"
            >
              <h3 className="flex items-center gap-2 font-display text-base font-semibold text-foreground">
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-accent/15 text-xs text-accent">
                  ✓
                </span>
                {p.icon}
              </h3>
              <p className="mt-2 text-sm leading-6 text-foreground/70">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Closing note */}
      <section className="mt-12 rounded-2xl border border-accent/25 bg-card/60 p-6 sm:p-8">
        <h2 className="font-display text-2xl font-bold text-foreground">
          Thank You
        </h2>
        <p className="mt-3 text-sm leading-7 text-foreground/80">
          Thank you for trusting us with a decision this important. We don't take that
          lightly. Have a story of your own, a correction to share, or a topic you wish we'd
          cover? Visit our{" "}
          <Link title="Contact the LifeWestWay editorial team" to="/contact" className="font-semibold text-accent hover:underline">
            Contact Us
          </Link>{" "}
          page — we read every message.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link title="Contact the LifeWestWay editorial team"
            to="/contact"
            className="inline-flex items-center rounded-md bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground transition-colors hover:bg-accent/90"
          >
            Contact our team →
          </Link>
          <Link title="Read the LifeWestWay editorial process"
            to="/editorial-process"
            className="inline-flex items-center rounded-md border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-accent hover:text-accent"
          >
            How we work
          </Link>
        </div>
      </section>

      <ArticleDisclaimer className="mt-12" />
    </div>
  );
}
